import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Loading from "../components/Loading";
import { Button, Link, TextField, Typography } from "@mui/material";
import { ErrorMessage, Form, Formik, FormikProps } from "formik";
import { NavLink } from "react-router-dom";
import { AppendIfPrefixExists } from "../modules/sitePaths";
import ShiOneLogo from "shared-ui/src/components/ShiOneLogo";
import AuthPageContainer from "./AuthPageContainer";
import { CreateProfile } from "lib/ShiOneClient";
import { postRestMyUserProfile } from "../api/registrationStore";
import { useProvisioningData } from "../api/useProvisioningData";

interface RegisterFormValues {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  extension: string;
}

type RegisterFormErrors = Partial<Record<keyof RegisterFormValues, string>>;

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export default function RegisterPage(): React.JSX.Element {
  const { user, isLoading } = useAuth0();
  const [submitComplete, setSubmitComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: provisioningData, isLoading: provisioningLoading } = useProvisioningData();

  const auth0Email = asString(user?.email);
  const auth0GivenName = asString(user?.["given_name"]);
  const auth0FamilyName = asString(user?.["family_name"]);
  const auth0PhoneNumber = asString(user?.["phone_number"]);

  const defaultFirstName = asString(provisioningData?.firstName);
  const defaultLastName = asString(provisioningData?.lastName);
  const defaultPhoneNumber = asString(provisioningData?.phoneNumber);
  const defaultExtension = asString(provisioningData?.extension);

  if (isLoading || provisioningLoading) {
    return <Loading />;
  }

  const initialInputs: RegisterFormValues = {
    email: asString(provisioningData?.email) || auth0Email,
    firstName: defaultFirstName || auth0GivenName,
    lastName: defaultLastName || auth0FamilyName,
    phoneNumber: defaultPhoneNumber || auth0PhoneNumber,
    extension: defaultExtension,
  };

  const handleSubmit = async (values: RegisterFormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      await postRestMyUserProfile(new CreateProfile(values));
      setSubmitComplete(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  function validate(values: RegisterFormValues): RegisterFormErrors {
    const errors: RegisterFormErrors = {};

    if (!values.email) {
      errors.email = "Required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = "Invalid email address";
    } else if (values.email.length > 50) {
      errors.email = "Email must be 50 characters or fewer";
    }

    if (!values.firstName) {
      errors.firstName = "First name is required";
    } else if (values.firstName.length > 20) {
      errors.firstName = "First name must be 20 characters or fewer";
    }
    if (!values.lastName) {
      errors.lastName = "Last name is required";
    } else if (values.lastName.length > 20) {
      errors.lastName = "Last name must be 20 characters or fewer";
    }
    if (!values.phoneNumber) {
      errors.phoneNumber = "Phone number is required";
    } else if (values.phoneNumber.length > 25) {
      errors.phoneNumber = "Phone number must be 25 characters or fewer";
    }
    if (values.extension && values.extension.length > 10) {
      errors.extension = "Extension must be 10 characters or fewer";
    }

    return errors;
  }

  return (
    <AuthPageContainer>
      <ShiOneLogo />
      <Typography variant="h6">
        {!submitComplete
          ? "Profile Registration"
          : "Registration Complete!"}
      </Typography>
      {!submitComplete ? (
        <Formik<RegisterFormValues>
          initialValues={initialInputs}
          validate={validate}
          onSubmit={handleSubmit}
        >
          {(props: FormikProps<RegisterFormValues>) => {
            const { values, touched, errors, handleChange, handleBlur } = props;

            return (
              <Form>
                <TextField
                  label="Email"
                  id="email"
                  type="email"
                  name="email"
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={
                    <span>
                      If this is incorrect, please&nbsp;
                      <Link style={{ textDecoration: "none" }} href="/logout">
                        log out
                      </Link>
                      &nbsp;and log back in with the correct email address.
                    </span>
                  }
                  required
                  disabled
                />
                <TextField
                  label="First Name"
                  id="firstName"
                  name="firstName"
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={<ErrorMessage name="firstName" />}
                  error={Boolean(errors.firstName && touched.firstName)}
                  required
                  disabled={isSubmitting}
                  slotProps={{
                    htmlInput: { maxLength: 20 }
                  }}
                />
                <TextField
                  label="Last Name"
                  name="lastName"
                  id="lastName"
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={<ErrorMessage name="lastName" />}
                  error={Boolean(errors.lastName && touched.lastName)}
                  required
                  disabled={isSubmitting}
                  slotProps={{
                    htmlInput: { maxLength: 20 }
                  }}
                />
                <TextField
                  label="Phone Number"
                  name="phoneNumber"
                  id="phoneNumber"
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  value={values.phoneNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={<ErrorMessage name="phoneNumber" />}
                  error={Boolean(errors.phoneNumber && touched.phoneNumber)}
                  required
                  disabled={isSubmitting}
                  slotProps={{
                    htmlInput: { maxLength: 25 }
                  }}
                />
                <TextField
                  label="Phone Extension"
                  name="extension"
                  id="extension"
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  value={values.extension}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={<ErrorMessage name="extension" />}
                  error={Boolean(errors.extension && touched.extension)}
                  disabled={isSubmitting}
                  slotProps={{
                    htmlInput: { maxLength: 10 }
                  }}
                />
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{ mt: 3 }}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : "Finish Registration"}
                </Button>
              </Form>
            );
          }}
        </Formik>
      ) : (
        <div>
          <NavLink
            to={AppendIfPrefixExists("/logout")}
            className={({ isActive }) =>
              isActive ? "landing-btn active" : "landing-btn"
            }
          >
            Return to Login
          </NavLink>
        </div>
      )}
    </AuthPageContainer>
  );
}
